import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeft,
    ArrowRight,
    CheckCircle2,
    XCircle,
    ShieldCheck,
    Clock4,
    Sparkles,
    Heart,
    BadgeCheck,
} from "lucide-react";
import CartoonSticker from "@/components/site/CartoonStickers";
import { Reveal, Stagger, Item } from "@/components/site/Reveal";
import { findService, SERVICE_CATALOG } from "@/data/serviceCatalog";

const TAG_STYLES = {
    FREE: "bg-emerald-50 text-emerald-700",
    PAID: "bg-[#FDF1E7] text-[#F26B1F]",
    QUOTE: "bg-[#0E2A47]/5 text-[#0E2A47]",
};

export default function ServiceDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const service = findService(slug);

    if (!service) {
        return (
            <div className="section">
                <div className="container-atm">
                    <div className="card-3d mx-auto max-w-2xl p-10 text-center">
                        <h1 className="text-3xl font-semibold">Service not found</h1>
                        <p className="mt-3 text-[15px] text-[#475569]">
                            That service link doesn't exist. Browse all services below.
                        </p>
                        <Link to="/services" className="btn-primary mt-8 inline-flex">
                            All Services <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const idx = SERVICE_CATALOG.findIndex((s) => s.slug === slug);
    const prev = idx > 0 ? SERVICE_CATALOG[idx - 1] : null;
    const next = idx < SERVICE_CATALOG.length - 1 ? SERVICE_CATALOG[idx + 1] : null;

    return (
        <motion.div
            key={slug}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            data-testid={`service-detail-${slug}`}
        >
            {/* HERO */}
            <section className="relative gradient-warm overflow-hidden pt-12 pb-16 md:pt-16 md:pb-20">
                {/* Decorative stickers — tucked into the FAR right edge so they
                    never collide with hero text on any breakpoint. */}
                <CartoonSticker name={service.sticker} width={120} rotate={-6} className="hidden xl:inline-flex top-10 right-6" />
                {service.accentStickers?.[0] && (
                    <CartoonSticker name={service.accentStickers[0]} width={68} rotate={8} className="hidden xl:inline-flex bottom-10 right-10 float-y-slow" />
                )}

                <div className="container-atm relative">
                    <div className="flex items-center justify-between gap-4">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="inline-flex items-center gap-2 rounded-full border border-[#ECE4D3] bg-white/70 px-4 py-2 text-sm font-semibold text-[#0E2A47] backdrop-blur transition-all hover:-translate-x-0.5 hover:bg-white"
                            data-testid="service-detail-back"
                        >
                            <ArrowLeft size={14} /> Back
                        </button>
                        <Link
                            to="/services"
                            className="text-xs font-semibold uppercase tracking-widest text-[#0E2A47]/60 hover:text-[#0E2A47]"
                        >
                            All services
                        </Link>
                    </div>

                    <div className="mt-10 max-w-3xl">
                        <Reveal>
                            <span
                                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest ${TAG_STYLES[service.tag]}`}
                            >
                                {service.tag}
                                <span className="h-1 w-1 rounded-full bg-current opacity-60" />
                                {service.eyebrow}
                            </span>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <h1 className="mt-5 text-4xl font-semibold leading-[1.05] sm:text-5xl lg:text-6xl">
                                {service.title}
                            </h1>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-6 text-base text-[#475569] md:text-lg">
                                {service.summary}
                            </p>
                        </Reveal>

                        <Reveal delay={0.25}>
                            <div className="mt-8 flex flex-wrap items-center gap-4">
                                <Link
                                    to={`/book?service=${service.bookingKey}`}
                                    className="btn-primary"
                                    data-testid="service-detail-cta"
                                >
                                    {service.cta} <ArrowRight size={16} />
                                </Link>
                                <Link to="/services" className="btn-secondary">
                                    Compare services
                                </Link>
                            </div>
                        </Reveal>

                        <Reveal delay={0.32}>
                            <div className="mt-10 flex flex-wrap gap-6 rounded-3xl border border-[#ECE4D3] bg-white/70 p-5 backdrop-blur md:gap-10">
                                <div>
                                    <p className="text-[11px] font-bold uppercase tracking-widest text-[#F26B1F]">
                                        Price
                                    </p>
                                    <p className="mt-1 font-display text-3xl font-semibold text-[#0E2A47]">
                                        {service.price}
                                    </p>
                                    <p className="mt-1 text-xs text-[#475569]">
                                        {service.priceNote}
                                    </p>
                                </div>
                                {service.additionalTime && (
                                    <div className="max-w-xs">
                                        <p className="text-[11px] font-bold uppercase tracking-widest text-[#F26B1F]">
                                            Additional time
                                        </p>
                                        <p className="mt-1 inline-flex items-center gap-2 text-sm font-medium text-[#0E2A47]">
                                            <Clock4 size={14} /> Approval required
                                        </p>
                                        <p className="mt-1 text-xs text-[#475569]">
                                            {service.additionalTime}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* BODY */}
            <section className="section">
                <div className="container-atm grid gap-8 lg:grid-cols-12">
                    {/* Best for */}
                    <Reveal className="lg:col-span-6">
                        <div className="card-3d h-full p-7 md:p-9">
                            <div className="flex items-center gap-3">
                                <span className="rounded-2xl bg-[#FDF1E7] p-2.5 text-[#F26B1F]">
                                    <Heart size={20} strokeWidth={1.6} />
                                </span>
                                <h2 className="text-2xl font-semibold">Best for</h2>
                            </div>
                            <Stagger className="mt-6 grid gap-3 sm:grid-cols-2">
                                {service.bestFor.map((b) => (
                                    <Item
                                        key={b}
                                        className="flex items-start gap-2 rounded-xl bg-[#FAF6EE] px-4 py-3 text-sm font-medium text-[#0E2A47]"
                                    >
                                        <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F26B1F] text-white">
                                            <CheckCircle2 size={12} />
                                        </span>
                                        {b}
                                    </Item>
                                ))}
                            </Stagger>
                        </div>
                    </Reveal>

                    {/* Includes */}
                    <Reveal delay={0.05} className="lg:col-span-6">
                        <div className="card-3d h-full p-7 md:p-9">
                            <div className="flex items-center gap-3">
                                <span className="rounded-2xl bg-[#0E2A47] p-2.5 text-white">
                                    <BadgeCheck size={20} strokeWidth={1.6} />
                                </span>
                                <h2 className="text-2xl font-semibold">What's included</h2>
                            </div>
                            <Stagger className="mt-6 space-y-3">
                                {service.includes.map((b) => (
                                    <Item
                                        key={b}
                                        className="flex items-start gap-3 text-[15px] text-[#0E2A47]"
                                    >
                                        <CheckCircle2
                                            size={18}
                                            className="mt-0.5 shrink-0 text-emerald-500"
                                        />
                                        <span>{b}</span>
                                    </Item>
                                ))}
                            </Stagger>
                        </div>
                    </Reveal>

                    {/* Excludes */}
                    {service.excludes && service.excludes.length > 0 && (
                        <Reveal delay={0.1} className="lg:col-span-6">
                            <div className="card-3d h-full p-7 md:p-9">
                                <div className="flex items-center gap-3">
                                    <span className="rounded-2xl bg-rose-50 p-2.5 text-rose-600">
                                        <XCircle size={20} strokeWidth={1.6} />
                                    </span>
                                    <h2 className="text-2xl font-semibold">
                                        What's not included
                                    </h2>
                                </div>
                                <Stagger className="mt-6 space-y-3">
                                    {service.excludes.map((b) => (
                                        <Item
                                            key={b}
                                            className="flex items-start gap-3 text-[15px] text-[#475569]"
                                        >
                                            <XCircle
                                                size={18}
                                                className="mt-0.5 shrink-0 text-rose-400"
                                            />
                                            <span>{b}</span>
                                        </Item>
                                    ))}
                                </Stagger>
                            </div>
                        </Reveal>
                    )}

                    {/* Move-day packages */}
                    {service.packages && (
                        <Reveal delay={0.15} className="lg:col-span-12">
                            <div className="card-3d p-7 md:p-9">
                                <div className="flex items-center gap-3">
                                    <span className="rounded-2xl bg-[#FDF1E7] p-2.5 text-[#F26B1F]">
                                        <Clock4 size={20} strokeWidth={1.6} />
                                    </span>
                                    <h2 className="text-2xl font-semibold">
                                        Choose your time block
                                    </h2>
                                </div>
                                <Stagger className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                                    {service.packages.map((p) => (
                                        <Item key={p.key}>
                                            <Link
                                                to={`/book?service=${p.key}`}
                                                className="block rounded-2xl border-2 border-[#ECE4D3] p-5 transition-all hover:-translate-y-1 hover:border-[#F26B1F] hover:bg-[#FDF1E7]"
                                                data-testid={`detail-tier-${p.key}`}
                                            >
                                                <p className="text-[11px] font-bold uppercase tracking-widest text-[#F26B1F]">
                                                    {p.label}
                                                </p>
                                                <p className="mt-2 font-display text-3xl font-semibold text-[#0E2A47]">
                                                    {p.price}
                                                </p>
                                                <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#0E2A47]">
                                                    Book this <ArrowRight size={12} />
                                                </p>
                                            </Link>
                                        </Item>
                                    ))}
                                </Stagger>
                            </div>
                        </Reveal>
                    )}

                    {/* Boundary */}
                    {service.boundary && (
                        <Reveal delay={0.18} className="lg:col-span-12">
                            <div className="rounded-3xl bg-[#0E2A47] p-7 text-white md:p-9">
                                <div className="flex items-start gap-4">
                                    <span className="rounded-2xl bg-[#F26B1F] p-2.5 text-white">
                                        <ShieldCheck size={20} strokeWidth={1.6} />
                                    </span>
                                    <div>
                                        <h3 className="text-lg font-semibold">
                                            Important boundary
                                        </h3>
                                        <p className="mt-2 text-[15px] leading-relaxed text-white/80">
                                            {service.boundary}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    )}
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="pb-24">
                <div className="container-atm">
                    <Reveal>
                        <div className="relative overflow-hidden rounded-[36px] gradient-navy px-8 py-14 text-white md:px-14 md:py-16">
                            {/* single corner accent only */}
                            <CartoonSticker name="check" width={64} rotate={-6} className="hidden lg:inline-flex right-10 top-10 float-y-slow" />

                            <div className="relative grid items-center gap-8 md:grid-cols-12">
                                <div className="md:col-span-7">
                                    <span className="eyebrow !text-[#F26B1F]">
                                        Ready when you are
                                    </span>
                                    <h3 className="mt-3 font-display text-3xl font-semibold leading-tight md:text-4xl">
                                        {service.cta} — confirm scope first, then we coordinate.
                                    </h3>
                                </div>
                                <div className="md:col-span-5 md:text-right">
                                    <Link
                                        to={`/book?service=${service.bookingKey}`}
                                        className="btn-primary !shadow-none"
                                    >
                                        {service.cta} <ArrowRight size={16} />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* PREV / NEXT */}
            <section className="pb-24">
                <div className="container-atm">
                    <div className="grid gap-4 md:grid-cols-2">
                        {prev ? (
                            <Link
                                to={`/services/${prev.slug}`}
                                className="card-3d group flex items-center gap-4 p-6 transition-all"
                                data-testid="service-detail-prev"
                            >
                                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#FAF6EE] text-[#0E2A47] transition-all group-hover:-translate-x-1 group-hover:bg-[#0E2A47] group-hover:text-white">
                                    <ArrowLeft size={16} />
                                </span>
                                <div>
                                    <p className="text-[11px] font-bold uppercase tracking-widest text-[#F26B1F]">
                                        Previous
                                    </p>
                                    <p className="mt-0.5 font-display text-base font-semibold text-[#0E2A47]">
                                        {prev.title}
                                    </p>
                                </div>
                            </Link>
                        ) : (
                            <div />
                        )}
                        {next ? (
                            <Link
                                to={`/services/${next.slug}`}
                                className="card-3d group flex items-center justify-end gap-4 p-6 text-right transition-all"
                                data-testid="service-detail-next"
                            >
                                <div>
                                    <p className="text-[11px] font-bold uppercase tracking-widest text-[#F26B1F]">
                                        Next service
                                    </p>
                                    <p className="mt-0.5 font-display text-base font-semibold text-[#0E2A47]">
                                        {next.title}
                                    </p>
                                </div>
                                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#FAF6EE] text-[#0E2A47] transition-all group-hover:translate-x-1 group-hover:bg-[#F26B1F] group-hover:text-white">
                                    <ArrowRight size={16} />
                                </span>
                            </Link>
                        ) : (
                            <div />
                        )}
                    </div>
                </div>
            </section>
        </motion.div>
    );
}
